import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Timestamp } from 'firebase/firestore';
import { Game } from '../types/types';

const formatDate = (date: Date): string => {
  return date.toLocaleDateString('fi-FI', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

interface ReservationFormProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (name: string, email: string, isPublicName: boolean) => Promise<{ success: boolean; message: string } | undefined>;
  onCancel: () => void;
  selectedGame: Game;
  expiresAt?: Timestamp;
  error?: string;
  mode?: 'LOTTERY' | 'FCFS';
}

const ReservationForm: React.FC<ReservationFormProps> = ({
  open,
  onClose,
  onConfirm,
  onCancel,
  selectedGame,
  expiresAt,
  error,
  mode = 'FCFS',
}) => {
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [isPublicName, setIsPublicName] = useState(true);
  const [timer, setTimer] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const response = await onConfirm(name, email, isPublicName);
      if (response?.success) {
        onClose();
      } else {
        console.error(response?.message);
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error(err);
      setIsSubmitting(false);
    }
  };

  const handleCancel = useCallback(() => {
    if (isSubmitting) return;
    onCancel();
    onClose();
  }, [onCancel, onClose, isSubmitting]);

  useEffect(() => {
    if (!open) {
      setName('');
      setEmail('');
      setTimer(null);
      setIsSubmitting(false);
      return;
    }

    if (expiresAt) {
      const expiresAtTime = expiresAt.toDate().getTime();
      const now = Date.now();
      const timeLeft = expiresAtTime - now;

      setTimer(timeLeft > 0 ? Math.floor(timeLeft / 1000) : null);
    } else {
      setTimer(null);
    }
  }, [open, expiresAt]);

  useEffect(() => {
    if (timer === null) return;

    if (timer <= 0) {
      handleCancel();
      return;
    }

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          handleCancel();
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timer, handleCancel]);

  return (
    <Dialog open={open} onClose={handleCancel}>
      <DialogTitle>{mode === 'FCFS' ? 'Varauksen vahvistus' : 'Osallistu arvontaan'}</DialogTitle>
      <DialogContent>
        <Typography>
          {formatDate(new Date(selectedGame.start))} {selectedGame.homeTeamName} - {selectedGame.awayTeamName}
        </Typography>
        {timer !== null && (
          <Typography color='error'>
            Aikaa jäljellä vahvistaa: {Math.floor(timer / 60)}:{('0' + (timer % 60)).slice(-2)}
          </Typography>
        )}
        <TextField
          autoFocus
          margin="dense"
          label="Nimi"
          type="text"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isSubmitting}
          autoComplete="name"
        />
        <TextField
          margin="dense"
          label="Sähköposti"
          type="email"
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isSubmitting}
          autoComplete="email"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={isPublicName}
              onChange={(e) => setIsPublicName(e.target.checked)}
              color="primary"
              disabled={isSubmitting}
            />
          }
          label="Nimeni saa näkyä varausnäkymässä"
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCancel}
          sx={{
            backgroundColor: "white",
            color: "primary.main",
            '&:hover': { backgroundColor: "#f0f0f0" },
          }}
          disabled={isSubmitting}
        >
          Peruuta varaus
        </Button>
        <Button
          onClick={handleConfirm}
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            '&:hover': { backgroundColor: "#a83245" },
            position: 'relative',
          }}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Vahvista'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReservationForm;
